import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton
} from "@mui/material";
import AutocompleteMultiSelect from "applications/signal/components/AutocompleteMultiSelect";
import {
  useSaveSignalMonitoringRecipients,
  useSignalGroupMembers,
  useGetSignalMonitoringRecipients,
  useGetSignalUserTeams
} from "applications/signal/hooks/gqlHooks";
import {
  signalMonitoringRecipientOptionsFormatter,
  signalMonitoringRecipientOptionsToInputFormatter,
  signalUserOptionsFormatter,
  userTeamRecipientOptionFormatter
} from "applications/signal/utils/dataUtils";
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import { MonitoringRecipientsManageModalData, MonitoringRecipientsManageModalSchema } from "./schema";
import { SignalMonitoringRecipient, SignalMonitoringSetting, SignalUser, SignalUserTeam } from "gql/graphql";

type Props = {
  monitoringSetting: SignalMonitoringSetting | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
  setSelectedRow: (row: SignalMonitoringSetting | null) => void;
}

export default function MonitoringRecipientsManageModal({ monitoringSetting, open, setOpen, setSnackBarMessage, setSelectedRow }: Props) {
  const [recipientsData, setRecipientData] = useState<string[]>([]);
  const {
    data: groupMemberResponse,
    fetching: groupMemberFetching,
    error: groupMemberError,
    fetchSignalGroupMembersAll
  } = useSignalGroupMembers();
  const {
    data: teamResponse,
    fetching: teamFetching,
    error: teamError,
    fetchSignalUserTeamsAll
  } = useGetSignalUserTeams();
  const {
    data: monitoringRecipientsResponse,
    fetching: monitoringRecipientsFetching,
    error: monitoringRecipientsError,
    fetchSignalMonitoringRecipients
  } = useGetSignalMonitoringRecipients();
  const { saveSignalMonitoringRecipients } = useSaveSignalMonitoringRecipients();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MonitoringRecipientsManageModalData>({
    resolver: zodResolver(MonitoringRecipientsManageModalSchema),
    defaultValues: {
      recipients: [],
    },
  });

  useEffect(() => {
    if (open && monitoringSetting) {
      fetchSignalGroupMembersAll();
      fetchSignalUserTeamsAll();
      fetchSignalMonitoringRecipients(monitoringSetting.id);
    }
  }, [open, monitoringSetting]);
  useEffect(() => {
    if (
      groupMemberResponse?.getSignalGroupMembers &&
      teamResponse?.getSignalUserTeams &&
      monitoringRecipientsResponse?.getSignalMonitoringRecipients
    ) {
      const teamRecipientCandidates = teamResponse.getSignalUserTeams.teams as SignalUserTeam[] || [];
      const memberRecipientCandidates = groupMemberResponse.getSignalGroupMembers.members as SignalUser[] || [];
      const allRecipientOptions = userTeamRecipientOptionFormatter(teamRecipientCandidates)
        .concat(signalUserOptionsFormatter(memberRecipientCandidates));
      setRecipientData(allRecipientOptions);
      reset({
        recipients: signalMonitoringRecipientOptionsFormatter(
          monitoringRecipientsResponse.getSignalMonitoringRecipients as SignalMonitoringRecipient[],
          teamRecipientCandidates,
          memberRecipientCandidates
        ),
      });
    }
    if (groupMemberError || teamError || monitoringRecipientsError) {
      setGlobalMessage({ severity: 'error', message: '受信者の取得に失敗しました。' });
    }
  }, [groupMemberResponse, groupMemberError, teamResponse, teamError, monitoringRecipientsResponse, monitoringRecipientsError]);

  const handleClickClose = () => {
    setSelectedRow(null);
    setOpen(false);
  };

  const onSubmit = async (data: MonitoringRecipientsManageModalData) =>  {
    const teamRecipientCandidates = teamResponse.getSignalUserTeams.teams as SignalUserTeam[] || [];
    const input = {
      signal_setting_id: String(monitoringSetting?.id),
      recipients: signalMonitoringRecipientOptionsToInputFormatter(data.recipients, teamRecipientCandidates),
    };
    const { data: result, error } = await saveSignalMonitoringRecipients({data: input});
    if (error) {
      setGlobalMessage({
        message: `受信者の更新に失敗しました。`,
        severity: "error",
      });
    } else {
      setSnackBarMessage({
        message: `受信者を更新しました。`,
        severity: "success",
      });
      handleClickClose();
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      onClose={handleClickClose}
    >
      <DialogTitle align="center">
        Signal受信者管理
      </DialogTitle>
      <IconButton
        onClick={handleClickClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.recipients}
          >
            <Controller
              name="recipients"
              control={control}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label="Signal受信者"
                  options={recipientsData || []}
                  value={field.value || []}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                />
              )}
            />
            <FormHelperText>
              {errors.recipients?.message}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            保存
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
